'use client';

import Link from 'next/link';
import { cn } from '@/lib/utils';
import { Check, Lock, Settings2 } from 'lucide-react';
import { LANDING_CONTENT } from '../constants/content';
import LandingReveal from './LandingReveal';
import { useLocale } from '@/features/marketing/hooks/useLocale';
import type { BilingualContent } from '@/features/marketing/hooks/useLocale';
import { Chip } from '@/features/marketing/ui/Chip';
import { PRICING_TYPE } from '@/features/marketing/constants/pricing-type';
import {
  PAPER_BAND,
  PAPER_FOCUS,
  PAPER_INK,
  PAPER_RULE,
  PAPER_VARIANT,
  PAPER_ACCENT_INK,
} from '@/features/marketing/ui/paper-tokens';
import { LANDING_PRIMARY_CTA_RECIPE } from '@/features/marketing/ui/cta-recipe';

// Plan slug for the registration query string (Judge 2 forensic masterplan finding
// 1 — WS2 intent taxonomy: intent=crm|bot|both, plan=basic|pro). Carrying the
// chosen tier through the CTA href is this lane's half of that fix; consuming it
// in RegisterForm/OnboardingWizard to preselect the plan is WS2, out of scope here.
const PLAN_SLUG: Record<string, 'basic' | 'pro'> = {
  Starter: 'basic',
  Pro: 'pro',
};

type Tier = (typeof LANDING_CONTENT.pricing.tiers)[number];

/**
 * FeatureGroups — R3 judge-E-verdict.md Decision 2/3. Starter carries the
 * complete inventory in 3 grouped columns (CRM+leads / team work / insights);
 * groups 1-2 sit side by side at `lg`, group 3 spans both — never the loud
 * shared-index card round 2 rendered separately (killed this round). Pro
 * (r3.5 §4 Lever A, `content.ts`) carries a Starter-parity checklist: the same
 * 3 category headings as a truthful one-line-per-group summary, plus its one
 * real "extra in Pro" group — single column, never `lg:grid-cols-2`.
 *
 * R5-P3 §5 "one row grammar" (Decision 10 [ADV-26]): heading/row text now
 * reads `PRICING_TYPE.groupHeading`/`row` instead of two local literal
 * treatments — groupHeading shrinks 16/22 -> 13/18 (eyebrow-scale), row
 * leading tightens 22 -> 24 (weight already matched); see pricing-type.ts's
 * census letters I/J.
 */
function FeatureGroups({ tier }: { tier: Tier }) {
  const { t } = useLocale();
  const isStarter = tier.name === 'Starter';

  return (
    // r3.5 §4 Lever B (r35-stepper-spec.md §4.2): row/heading density tightened
    // (gap-y-6->5, mb-3->2.5, row gap-2.5->2) — applied to BOTH tiers via this
    // shared component so Pro's new parity rows (content.ts Lever A, 2->4
    // groups) stack dense too, not just Starter's 11 rows.
    <div className={cn('grid gap-x-8 gap-y-5', isStarter && 'lg:grid-cols-2')}>
      {tier.featureGroups.map((group, i) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key -- static marketing config, never reordered
        <div key={i} className={cn('min-w-0', isStarter && i === 2 && 'lg:col-span-2')}>
          <p className={cn(PRICING_TYPE.groupHeading, PAPER_INK.muted.text, 'mb-2.5')}>
            {t(group.heading)}
          </p>
          <ul className="flex flex-col gap-2" aria-label={t(group.heading)}>
            {group.rows.map((row, j) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key -- static marketing config, never reordered
              <li key={j} className="flex items-start gap-3">
                {/* R7 paper flip: the checkmark chip's fill flips from a
                    LIGHTER wash-on-dark (#FFFFFF14) to a black-alpha
                    wash-on-paper — same "one rung more surface-like" intent,
                    opposite sign; a plain Tailwind opacity utility, not a
                    registry hex, so it needs no paper-tokens.ts entry. */}
                <div
                  className="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-black/[0.04]"
                  aria-hidden="true"
                >
                  <Check className={cn('w-3 h-3', PAPER_INK.muted.text)} />
                </div>
                <span className={cn(PRICING_TYPE.row, PAPER_INK.secondary.text)}>{t(row)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/**
 * TierCard — R5-P3 §5 "one row grammar" (Decision 10 [ADV-26]) + shared-grid
 * upper zone (checkpoint-1 `PricingUpperZoneSpecimen.tsx`, LEAD-approved
 * 2026-07-15 checkpoint-1 ruling 5: "PRICING zone approved as prototyped").
 * Anatomy unchanged top to bottom: name+badge -> tagline -> price+period ->
 * VAT label (derived, never hardcoded) -> limits (leads/users) -> feature
 * groups -> CTA. No fixed/equal height — "rows size to max content" (the
 * prior Alex decision) still holds; it is now enforced by CSS subgrid
 * across BOTH cards instead of two independently-tuned flows that only
 * coincidentally lined up (r3.5 §4's old |ΔH|<=60px height-parity math is
 * superseded by this: every upper-zone row AND the CTA now align on
 * identical lines across cards, not just "close enough" total heights).
 * Feature-check icons stay neutral (PAPER_INK.muted, R7 paper flip -- was
 * DARK_INK.tertiary) on BOTH tiers — amber is reserved for Pro's badge/
 * limit-values/CTA only (R7: the card-level amber border/glow the prior
 * paragraph names is DROPPED — see this function's own body comment).
 *
 * SHARED-GRID MECHANISM: the parent (`PricingSectionInner`) defines 7
 * explicit row tracks (`lg:[grid-template-rows:repeat(7,auto)]`); each
 * card here is ITSELF a subgrid spanning all 7 (`lg:grid
 * lg:grid-rows-[subgrid] lg:[grid-row:1/8]`), so 6 real upper-zone rows
 * (name+badge / tagline / price+period / VAT / limits-leads / limits-users)
 * plus ONE tail track (feature groups + CTA) size to the MAX of both
 * cards' content — Starter/Pro baselines land on identical lines with zero
 * JS measurement and zero fixed heights. Every shared-row child below
 * carries `self-start` so it does not stretch to fill a row a sibling card
 * made taller. The tail track has NO implicit sub-tracks of its own (CSS
 * Grid Level 2: a subgridded axis's track list is fixed, inherited from its
 * span) — `PricingUpperZoneSpecimen.tsx`'s corrected file header documents
 * the measured defect (everything piling into the last inherited track)
 * this ONE tail wrapper avoids; `mt-auto` inside it pins the CTA to the
 * track's bottom so both cards' CTAs land on the same line (checkpoint-1
 * measured: "both CTAs at y=1089").
 *
 * BROWSER-BAR DECISION (named per this lane's brief): the subgrid classes
 * above are gated behind `supports-[grid-template-rows:subgrid]:` (Tailwind
 * v3.4 arbitrary `@supports` variant, confirmed via Context7 —
 * `supports-[property:value]:utility` generates an `@supports` rule; a
 * bare `supports-[grid-template-rows]` would only test the PROPERTY, which
 * has existed since CSS Grid L1, so the VALUE form is required to actually
 * gate on subgrid support: Chrome 117+ / Safari 16+ / Firefox 71+). A
 * browser that fails the `@supports` query never receives `grid`/
 * `grid-rows-[subgrid]`/`[grid-row:1/8]` at all, so each card silently
 * falls back to being a plain block-flow item in its own `lg:grid-cols-2`
 * cell — today's SHIPPED independent-card flow (functional, just not
 * baseline-aligned across cards), never a broken/overlapping layout. See
 * `PricingSectionInner` for the matching parent-side gate on
 * `grid-template-rows:repeat(7,auto)`.
 */
function TierCard({
  tier,
  t,
  orderClassName,
}: {
  tier: Tier;
  t: (content: BilingualContent) => string;
  orderClassName: string;
}) {
  const isPopular = tier.popular === true;
  const planSlug = PLAN_SLUG[tier.name] ?? 'basic';
  // 2026-07-18 slop-audit revision pass FIX 1 (CRITICAL, amber-on-paper
  // contrast): was `text-[#FBBF24]` — 1.77:1 against this card's paper fill,
  // well under WCAG AA's 4.5:1 text floor. `PAPER_ACCENT_INK` is the
  // darkened, contrast-proven amber-as-text token (`ui/paper-tokens.ts`);
  // the Chip "Popular" badge's own amber FILL (via `accentContext="pricing"`)
  // is unaffected — this only re-inks the limit VALUE text.
  const limitValueColor = isPopular ? PAPER_ACCENT_INK.text : PAPER_INK.primary.text;

  return (
    // R7 paper flip: the house `Surface` primitive is dark-hardcoded
    // (DARK_SURFACE.base, no paper variant) — per the R7 design contract's
    // "NO card-in-card (gate 4): flatten any nested surface" instruction,
    // this drops Surface entirely for a plain hairline-ruled paper `div`
    // instead of forking/extending an unowned primitive. The popular tier's
    // former card-level amber ring+glow is ALSO dropped here — the "Popular"
    // Chip badge below is the tier's ONE amber accent device (design
    // contract §2 "amber marker within accent budget... ONE device, not a
    // filled amber card"); both tiers now share the identical hairline
    // paper surface, distinguished only by that badge.
    <div
      data-stroke-role="card"
      className={cn(
        'relative min-w-0 rounded-[12px] border p-6 lg:p-8',
        PAPER_RULE.hairline.border,
        PAPER_BAND.base.bg,
        orderClassName,
        'lg:supports-[grid-template-rows:subgrid]:grid',
        'lg:supports-[grid-template-rows:subgrid]:grid-rows-[subgrid]',
        'lg:supports-[grid-template-rows:subgrid]:[grid-row:1/8]'
      )}
    >
      {/* Row 1 (shared) — plan name + badge. */}
      <div className="flex items-start justify-between gap-3 mb-2 self-start">
        <h3 className={cn(PRICING_TYPE.tierName, PAPER_INK.primary.text)}>{tier.name}</h3>
        {isPopular && 'badge' in tier && (
          // "Popular" badge — checkpoint-1 RESOLVED ruling 5: converts to the
          // Chip status/accent recipe (kills the last bespoke <span> pill).
          // R5-P3 post-review fix round: Chip's `accentContext` union widened
          // to add a literal `'pricing'` value (Chip.tsx, same file both
          // reviews flagged as the needed cross-lane change) — this badge now
          // reads the REAL `tone="accent"` arm directly (the actual
          // `#f59e0b` amber recipe) instead of replicating its old bespoke
          // hex trio (`#F59E0B3D`/`#F59E0B14`/`#FBBF24`) via a `className`
          // override on top of the neutral-status shape. `check-landing-
          // band-hue.mjs`'s general "buttons/chips/pricing badges" decision-
          // amber allowance already covers Chip.tsx's own accent literals
          // (used identically by the hero/final-CTA poles), so this needs
          // no gate change.
          <Chip
            variant="status"
            tone="accent"
            accentContext="pricing"
            label={t(tier.badge as BilingualContent)}
          />
        )}
      </div>

      {/* Row 2 (shared) — tagline. */}
      <p className={cn(PRICING_TYPE.row, PAPER_INK.muted.text, 'mb-6 self-start')}>
        {t(tier.tagline)}
      </p>

      {/* Row 3 (shared) — price + period, one baseline, largest number on
          the card. */}
      <div className="flex items-baseline gap-1.5 flex-wrap self-start">
        <span className={cn(PRICING_TYPE.price, PAPER_INK.primary.text)}>{tier.price}</span>
        <span className={cn(PRICING_TYPE.row, PAPER_INK.muted.text)}>{t(tier.period)}</span>
      </div>

      {/* Row 4 (shared) — VAT label, derived fact (plans.ts priceIncludesVat
          -> plan-facts.ts formatVatLabel -> tier.vatLabel), never a
          hardcoded rate/string here. Weight intentionally 400 now (was 600)
          — pricing-type.ts census letter F, Decision 10 "one row grammar". */}
      <p className={cn(PRICING_TYPE.row, PAPER_INK.secondary.text, 'mt-[6px] mb-6 self-start')}>
        {t(tier.vatLabel)}
      </p>

      {/* Rows 5-6 (shared) — limits table, split into two independent
          shared rows (was one `border-y divide-y` wrapper) so leads/users
          each align across cards on their OWN grid line — a single wrapper
          div would collapse both into one shared cell.
          `PricingUpperZoneSpecimen.tsx` prototyped this exact split. The
          border-t / border-t+border-b pair reproduces the old wrapper's
          "one bordered box, one internal divider" read.
          R7 paper flip: inner dividers use PAPER_RULE.soft — one step
          fainter than the card's own outer PAPER_RULE.hairline edge, so the
          nested rule reads as a lighter internal seam, not a second card
          border. */}
      <div
        className={cn(
          'flex items-baseline justify-between gap-3 border-t pt-3 pb-3 self-start',
          PAPER_RULE.soft.border
        )}
      >
        <span className={cn(PRICING_TYPE.row, PAPER_INK.muted.text)}>
          {t(tier.limitsPanel.leads.label)}
        </span>
        <span className={cn(PRICING_TYPE.limitValue, limitValueColor)}>
          {t(tier.limitsPanel.leads.value)}
        </span>
      </div>
      <div
        className={cn(
          'flex items-baseline justify-between gap-3 border-t border-b pt-3 pb-3 mb-6 self-start',
          PAPER_RULE.soft.border
        )}
      >
        <span className={cn(PRICING_TYPE.row, PAPER_INK.muted.text)}>
          {t(tier.limitsPanel.users.label)}
        </span>
        <span className={cn(PRICING_TYPE.limitValue, limitValueColor)}>
          {t(tier.limitsPanel.users.value)}
        </span>
      </div>

      {/* The 7th shared track: ONE tail wrapper (see TierCard's own docblock
          for why a subgrid axis has no implicit tracks). Groups stay
          content-driven inside it; `mt-auto` pins the CTA to the track
          bottom so both cards' CTAs land on the same line. */}
      <div className="lg:flex lg:min-h-0 lg:flex-col">
        <div className="mb-8">
          <FeatureGroups tier={tier} />
        </div>

        {/* CTA — PRICING_TYPE.cta (15px/600), "button system" role, exempt
            from a shared line-height (flex-centered, `h-12` fixed button
            height governs vertical size). Hover deepens alpha only. */}
        <Link
          href={`/auth/register?intent=crm&plan=${planSlug}`}
          prefetch={false}
          // v2 bio-link engagement pipeline (contract 3/6): both tiers share
          // the one canonical event_name "cta_pricing" — the row is which
          // tier converted, not a per-tier name.
          data-track="cta_pricing"
          // 2026-08-13 true-320 RO: the tier CTAs wrapped to 2 lines. The full
          // label (with the tier name) stays the button's ACCESSIBLE name at
          // every width, so the narrow-viewport visual label below may drop the
          // tier without costing a screen-reader user anything.
          aria-label={t(tier.cta)}
          className={cn(
            PRICING_TYPE.cta,
            'w-full inline-flex items-center justify-center h-12 px-5 sm:px-6 rounded-xl lg:mt-auto',
            // R6 (judge de-slop round): the popular arm adopts
            // LANDING_PRIMARY_CTA_RECIPE wholesale (color/elevation/hover/
            // active/transition/focus) instead of a hand-typed amber copy --
            // its own outline-based focus replaces the ring this element
            // used to share with the non-popular arm below (the shared ring
            // classes moved into that arm only, since it's a different,
            // non-amber treatment the recipe doesn't cover).
            // R7 paper flip: non-popular CTA converts to the design
            // contract's binding secondary/outline recipe —
            // PAPER_RULE.hairline.border + PAPER_INK.primary.text, hover =
            // alpha overlay (hover:bg-black/5), ring-based focus using
            // PAPER_FOCUS paired with the card's own PAPER_BAND.base
            // ring-offset (mirrors DARK_VARIANT.surfaceRingOffset's role for
            // the pre-flip dark Surface card).
            isPopular
              ? LANDING_PRIMARY_CTA_RECIPE
              : [
                  // 2026-07-18 slop-audit revision pass FIX 4 (LOW, gate 26):
                  // added a distinct `active:` state (was hover-only) — one
                  // consistent recipe with the other 2 secondary/outline CTAs
                  // (hero secondary, DemoSuccessCard).
                  'border',
                  PAPER_RULE.hairline.border,
                  PAPER_INK.primary.text,
                  'hover:bg-black/5 active:bg-black/10',
                  'transition-colors duration-[var(--motion-fast)]',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                  PAPER_FOCUS.ring,
                  PAPER_VARIANT.baseRingOffset,
                ]
          )}
        >
          {/* Same CSS-only swap as the hero/final CTAs at the same
              `min-[400px]` breakpoint. These buttons sit inside a card, so at
              320 they are ~230px wide rather than 280 — the tightest of the
              four sites, which is why the tier variant is shorter than the
              hero's. Measured `lines=1` at true 320 RO. Label span only; no
              layout change. */}
          <span className="min-[400px]:hidden">{t(tier.ctaShort)}</span>
          <span className="hidden min-[400px]:inline">{t(tier.cta)}</span>
        </Link>
      </div>
    </div>
  );
}

function PricingSectionInner() {
  const { pricing } = LANDING_CONTENT;
  const { t } = useLocale();

  // tiers[0] = Starter, tiers[1] = Pro (popular)
  const [starter, pro] = pricing.tiers;

  return (
    <section
      id={pricing.id}
      // R7 paper flip (design contract "PricingSection -> paper deep band"):
      // was warm-retinted DARK_BAND.pricing (R6 registry, #110F0B) — now
      // PAPER_BAND.deep, the darkest paper step, so the flat hairline tier
      // cards above (PAPER_BAND.base, the lightest step) read as distinctly
      // lighter surfaces sitting on top of it — the identical "card one rung
      // lighter than its band" relationship the pre-flip dark pairing had
      // (DARK_SURFACE.base was itself slightly lighter than DARK_BAND.pricing).
      // No amber wash; amber stays component-scale only, on the cards below.
      // R6 rhythm (judge §2.9, three-beat interlude-to-Bot handoff): bottom
      // padding compresses to pt-24 pb-12 lg:pt-32 lg:pb-16 (was pt-20
      // lg:pt-32 pb-9, a flat 36px pb both breakpoints) -- Pricing's half of
      // "compresses into its priced divider." The seam bubble itself still
      // renders OUTSIDE this section, in F-lane's interleaved divider slot in
      // LandingPage (r3-debate-rulings.md Harm. b) -- FLAGGED for a live
      // re-check: its clearance below Pricing needs re-verifying against
      // this new pb-12/lg:pb-16 (was pb-9/36px both breakpoints), AND against
      // the R7 paper band swap (INTEGRATION lane owns the seam/divider). No
      // incoming border-t: the preceding NarrativeHandoff bridge (#3) already
      // owns the single seam above — a second line here would double it.
      className={cn(
        'relative pt-24 pb-12 lg:pt-32 lg:pb-16 overflow-hidden [scroll-margin-top:80px] md:[scroll-margin-top:88px]',
        PAPER_BAND.deep.bg
      )}
    >
      <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-8">
        {/* Single group reveal for the whole section (Decision "reduced-motion
            composition": one LandingReveal for header + setup + cards + Stripe,
            delay 0 — never a cascading sequence of separately-delayed reveals). */}
        <LandingReveal>
          {/* Section header — serif display voice (Decision 1/2,
              PRICING_TYPE.sectionH2): every section H2 on the page unifies
              to `landing-serif-display` at the 34->40px ramp; this is just
              the Pricing instantiation (amendment §7 conformance matrix). */}
          <div className="max-w-[720px] mx-auto text-center mb-8 lg:mb-10">
            <h2 className={cn(PRICING_TYPE.sectionH2, PAPER_INK.primary.text, 'mb-4 lg:mb-5')}>
              {t(pricing.headline)}
            </h2>
            {/* Subtitle — reclassed onto PRICING_TYPE.row (Decision 10 "one
                row grammar"). Drops the old lg:text-lg/lg:leading-[1.55]
                upsize (18px/1.55 at lg) down to the flat 16/24 every other
                body-scale node in this section now shares — the registry is
                "the ONLY size source inside the section scope" (amendment
                §5/[ADV-26]), so a per-node breakpoint variant here would be
                exactly the un-registered exception the §8.4 gate exists to
                kill. */}
            <p className={cn(PRICING_TYPE.row, PAPER_INK.secondary.text)}>{t(pricing.sub)}</p>
          </div>

          {/* Setup strip — the header-to-cards bridge (Decision 5): a non-card
              hairline strip, not another slab competing with the cards below. */}
          <div
            className={cn(
              'max-w-6xl mx-auto border-y py-5 mb-6 lg:mb-8',
              PAPER_RULE.hairline.border
            )}
          >
            <div className="flex items-center gap-3 justify-start sm:justify-center text-left sm:text-center">
              <Settings2
                className={cn('w-6 h-6 flex-shrink-0', PAPER_INK.muted.text)}
                aria-hidden="true"
              />
              {/* pricing-type.ts's own header flagged this exact node
                  ("currently 18/28/600") as an open P3 decision — resolved
                  here: `tierName` is the closest registered role by value
                  (same 18px/600), a 4px leading flatten (28->24) traded for
                  registry membership. */}
              <p className={cn(PRICING_TYPE.tierName, PAPER_INK.primary.text)}>
                {t(pricing.setupPromise)}
              </p>
            </div>
          </div>

          {/*
            Both cards now live inside ONE outer grid (checkpoint-1
            `PricingUpperZoneSpecimen.tsx`, LEAD-approved 2026-07-15) — see
            TierCard's own docblock for the subgrid mechanism + the named
            browser-bar decision. `lg:[grid-template-rows:repeat(7,auto)]`
            (also `@supports`-gated, matching TierCard's own gate — a
            non-supporting browser gets NO explicit row template at all,
            which is harmless: with zero subgridded children the grid's
            auto-placement behaves exactly like today's shipped 2-col/1-row
            layout) defines the 7 shared row tracks each card's Surface
            subgrids into.

            Mobile ordering: Pro card first (popular-first for mobile cold
            visitors); desktop (lg+) Starter left -> Pro right. `order-*`
            now rides directly on each TierCard's own Surface (via
            `orderClassName`) rather than an intermediate wrapper div — a
            subgrid child must be a DIRECT child of this row-defining grid
            container. items-start: no equal-height stretch forced at the
            container level (Decision 3/5 "no fixed/min height" — still
            true of the outer grid item itself); r3.5 §4's old independent-
            flow |ΔH|<=60px height-parity math (2 content-density levers on
            FeatureGroups, still active below) is now SUPERSEDED for
            cross-card alignment by the shared-grid mechanism itself — every
            upper-zone row and the CTA line up structurally, not just
            approximately, once a supporting browser subgrids both cards.
          */}
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 lg:supports-[grid-template-rows:subgrid]:[grid-template-rows:repeat(7,auto)] gap-6 2xl:gap-8 items-start">
            <TierCard tier={starter} t={t} orderClassName="order-2 lg:order-1" />
            <TierCard tier={pro} t={t} orderClassName="order-1 lg:order-2" />
          </div>

          {/* Stripe line only — quiet mechanics row. Trial/card terms no longer
              duplicate here; they live once in the seam bubble below the fold
              of this section (F-lane's divider, Decision 8/9). */}
          <div className="max-w-6xl mx-auto mt-5 lg:mt-6 flex items-center justify-center gap-2">
            <Lock
              className={cn('w-4 h-4 flex-shrink-0', PAPER_INK.muted.text)}
              aria-hidden="true"
            />
            {/* Reclassed onto PRICING_TYPE.micro — exactly the destination
                pricing-type.ts's own header forward-declared. Documented
                shrink: 14/20 -> 13/18. */}
            <span className={cn(PRICING_TYPE.micro, PAPER_INK.muted.text)}>
              {t(pricing.assurance.securePayment)}
            </span>
          </div>
        </LandingReveal>
      </div>
    </section>
  );
}

export default function PricingSection() {
  return <PricingSectionInner />;
}
